import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {getRoomOfferAnalyticsContexts} from 'middleware/analytics/selectors'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getAnchorPriceNightlyPerRoom} from 'modules/sapiSearch/selectors'
import {getHotelResult} from 'modules/searchApi/selectors'
import {RootState} from 'store'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Card} from '@vio/atlas/src/components/designSystem/Card'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getDisplayPrice} from '@vio/core/src/price/business/price'
import {getSplitBookingDates} from '@vio/core/src/room/business/utils/getSplitBookingDates'
import {getSplitBookingRooms} from '@vio/core/src/room/business/utils/getSplitBookingRooms'
import {SplitBooking} from '@vio/core/src/room/types/room'
import {toDecreasePercentage} from '@vio/core/src/utils/number'
import {SplitBookingTimeline} from '@vio/shared/src/booking/SplitBooking/SplitBookingTimeline'
import {BookingTransitionMessage} from '@vio/shared/src/booking/SplitBooking/SplitBookingTransitionMessage'
import {SplitBookingCardHeader as CardHeader} from '@vio/shared/src/booking/SplitBookingCardHeader'
import {SplitBookingCardSubtitle as CardSubtitle} from '@vio/shared/src/booking/SplitBookingCardSubtitle'

import {RoomDetailsInfo} from './RoomDetailsInfo'
import {SplitBookingCardFooter} from './SplitBookingCardFooter'

interface Props {
  splitBooking: SplitBooking
  isOnlySplitBookingOfferAvailable?: boolean
  isCheapest?: boolean
  isOfferSoldOut?: boolean
}

const CardVariantWrapper: React.FC<{children: React.ReactNode}> = ({
  children
}) => {
  return (
    <Card
      borderRadius="xl"
      borderColorPath="border.neutral.secondary"
      dataId="defaultRoomCard"
    >
      {children}
    </Card>
  )
}

export const SplitBookingCard = ({
  splitBooking,
  isCheapest,
  isOnlySplitBookingOfferAvailable = false
}: Props) => {
  const dispatch = useDispatch()
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const splitBookingRooms = getSplitBookingRooms(splitBooking)

  const isOfferSoldOut = useGetIsOfferSoldOut({splitBooking})

  const splitBookingDates = getSplitBookingDates(splitBooking)
  const {checkIn, checkOut, splitDate} = splitBookingDates

  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))
  const {bundleId, offers, prices, cancellationPenalties} = splitBooking
  const {isMobile} = useDeviceLayout()
  const [{offer: firstOffer}, {offer: secondOffer}] = offers

  const displayPrice = getDisplayPrice(prices)
  const nightlyRate = displayPrice.chargeable.nightlyDisplayTotal

  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const savingPercentage = toDecreasePercentage(
    nightlyRate,
    anchorPrice,
    'floor'
  )

  const timelineDates: [string, string, string] | undefined = !isMobile
    ? [checkIn, splitDate, checkOut]
    : undefined
  // displaying images when at least one room has image
  const showImages = splitBookingRooms.some(({images}) => images.length)

  const analyticsContext = useSelector(state =>
    getRoomOfferAnalyticsContexts(state, bundleId, true)
  )

  useEffect(() => {
    if (isOfferSoldOut) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomOfferSoldOut,
          action: Action.Displayed,
          page: Page.Accommodation,
          team: Team.Select,
          analyticsContext
        })
      )
    }
  }, [analyticsContext, dispatch, isOfferSoldOut])

  return (
    <CardVariantWrapper>
      <Box padding={isMobile ? 's500' : 's600'}>
        <Box
          display="flex"
          flexDirection="column"
          gap={isMobile ? 's500' : 's600'}
        >
          <Box display="flex" flexDirection="column" gap="s100">
            <CardHeader
              isOnlySplitBookingOfferAvailable={
                isOnlySplitBookingOfferAvailable
              }
              savingPercentage={savingPercentage}
            />
            <CardSubtitle
              isOnlySplitBookingOfferAvailable={
                isOnlySplitBookingOfferAvailable
              }
              isCheapest={isCheapest}
              hasCustomStyle={isMobile}
            />
          </Box>

          <SplitBookingTimeline
            customGap
            topComponent={
              <RoomDetailsInfo
                childPolicy={hotel?.childPolicy}
                room={splitBookingRooms[0]}
                date={checkIn}
                midDate={splitDate}
                isCheckInDateAvailable
                mealPlan={firstOffer?.mealPlan}
                placement="top"
                showImage={showImages}
                hotelId={hotelId}
              />
            }
            bottomComponent={
              <RoomDetailsInfo
                childPolicy={hotel?.childPolicy}
                room={splitBookingRooms[1]}
                date={checkOut}
                midDate={splitDate}
                mealPlan={secondOffer?.mealPlan}
                placement="bottom"
                showImage={showImages}
                hotelId={hotelId}
              />
            }
            middleComponent={
              <BookingTransitionMessage
                midDate={splitDate}
                checkIn={checkIn}
                checkOut={checkOut}
                title={
                  <FormattedMessageWrapper
                    id="splitBooking.switchRoomTitle"
                    defaultMessage="Switch rooms to save"
                  />
                }
                message={
                  isCheapest && !isOnlySplitBookingOfferAvailable ? (
                    <FormattedMessageWrapper
                      id="splitBooking.switchRoomSubtitle"
                      defaultMessage="We got you the lowest price by splitting your stay across two separate bookings. Here’s how it works:"
                    />
                  ) : (
                    <FormattedMessageWrapper
                      id="splitBooking.roomSwitchTooltipSubtitle"
                      defaultMessage="This stay is split between two separate bookings. Here’s how it works:"
                    />
                  )
                }
              />
            }
            dates={timelineDates}
          />
        </Box>
      </Box>

      <SplitBookingCardFooter
        hotelId={hotelId}
        cancellationPenalties={cancellationPenalties}
        hotelTimeZone={hotel?.timezone}
        splitBooking={splitBooking}
        isOfferSoldOut={isOfferSoldOut}
      />
    </CardVariantWrapper>
  )
}
